import { useState, useEffect, useCallback } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { Flash, Modal, FormField, ModalActions, Pagination } from "../components/AdminUI";
import "../styles/admin.css";

const PRESET_COLORS = [
  "#E53935", "#43A047", "#3949AB", "#1E88E5", "#FB8C00",
  "#8E24AA", "#00ACC1", "#D81B60", "#7CB342", "#5E35B1",
  "#6D4C41", "#00897B", "#F4511E", "#333333",
];

export default function RouteManager() {
  const [routes, setRoutes]   = useState([]);
  const [depots, setDepots]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash]     = useState({ message: "", type: "success" });

  // フィルター
  const [searchQ, setSearchQ]         = useState("");
  const [depotFilter, setDepotFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 20;

  // 追加・編集モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [form, setForm] = useState({
    short_name: "", full_name: "", color: "", sort_number: "10", depot_id: "",
  });
  const [saving, setSaving] = useState(false);

  // ── データ取得 ──
  const loadDepots = useCallback(async () => {
    try {
      const res = await axios.get("/depots");
      setDepots(Array.isArray(res.data) ? res.data : []);
    } catch { setDepots([]); }
  }, []);

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/routes");
      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "路線の取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepots();
    loadRoutes();
  }, [loadDepots, loadRoutes]);

  function showFlash(message, type = "success") {
    setFlash({ message, type });
  }

  // ── フィルタリング・ページネーション ──
  const filtered = routes.filter((r) => {
    const q = searchQ.toLowerCase();
    const matchQ = !searchQ || r.short_name.toLowerCase().includes(q) || r.full_name.toLowerCase().includes(q);
    const matchD = !depotFilter || String(r.depot_id) === String(depotFilter);
    return matchQ && matchD;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage   = Math.min(currentPage, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  function handleSearchChange(v) { setSearchQ(v); setCurrentPage(1); }
  function handleDepotChange(v)  { setDepotFilter(v); setCurrentPage(1); }

  // ── モーダル操作 ──
  function openAdd() {
    setEditRoute(null);
    setForm({ short_name: "", full_name: "", color: "", sort_number: "10", depot_id: "" });
    setModalOpen(true);
  }

  function openEdit(route) {
    setEditRoute(route);
    setForm({
      short_name:  route.short_name,
      full_name:   route.full_name,
      color:       route.color ?? "",
      sort_number: String(route.sort_number ?? 10),
      depot_id:    String(route.depot_id),
    });
    setModalOpen(true);
  }

  function closeModal() { setModalOpen(false); setEditRoute(null); }

  async function handleSave() {
    if (!form.short_name.trim()) { showFlash("略称は必須です", "error"); return; }
    if (!form.full_name.trim())  { showFlash("正式名は必須です", "error"); return; }
    if (!form.depot_id)          { showFlash("営業所は必須です", "error"); return; }
    if (form.color && !/^#[0-9A-Fa-f]{6}$/.test(form.color)) {
      showFlash("カラーコードは #RRGGBB 形式で入力してください", "error"); return;
    }

    setSaving(true);
    const payload = {
      short_name:  form.short_name.trim(),
      full_name:   form.full_name.trim(),
      color:       form.color.trim() || null,
      sort_number: Number(form.sort_number),
      depot_id:    Number(form.depot_id),
    };

    try {
      if (editRoute) {
        await axios.put(`/admin/routes/${editRoute.id}`, payload);
        const depot = depots.find((d) => d.id === payload.depot_id);
        setRoutes((prev) =>
          prev.map((r) => r.id === editRoute.id ? { ...r, ...payload, depot: depot ?? r.depot } : r)
        );
        showFlash("更新しました");
      } else {
        const res = await axios.post("/admin/routes", payload);
        const depot = depots.find((d) => d.id === payload.depot_id);
        setRoutes((prev) => [{ ...res.data, depot: depot ?? null }, ...prev]);
        showFlash("追加しました");
      }
      closeModal();
    } catch (e) {
      const errors = e.response?.data?.errors;
      if (errors) {
        showFlash(Object.values(errors).flat()[0], "error");
      } else {
        showFlash(e.response?.data?.message ?? "保存に失敗しました", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──
  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "路線管理" }]} />

      <Flash message={flash.message} type={flash.type} onClose={() => setFlash({ ...flash, message: "" })} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">路線管理</h2>
          <span className="adm-badge adm-badge--violet">admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>
          ＋ 路線を追加
        </button>
      </div>

      {/* フィルター */}
      <div className="adm-filter-box">
        <div className="adm-filter-row">
          <div className="adm-filter-field">
            <span className="adm-filter-label">検索</span>
            <input
              type="text"
              className="adm-input"
              placeholder="略称・正式名"
              value={searchQ}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ minWidth: 180 }}
            />
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">営業所</span>
            <select
              className="adm-select"
              value={depotFilter}
              onChange={(e) => handleDepotChange(e.target.value)}
              style={{ minWidth: 130 }}
            >
              <option value="">全営業所</option>
              {depots.map((d) => (
                <option key={d.id} value={d.id}>{d.short_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* テーブル */}
      <div className="adm-card">
        <div className="adm-card__header">
          路線一覧
          <span className="adm-card__header-sub">{filtered.length} 件</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                {["ID", "略称", "路線名", "カラー", "表示順", "営業所", "登録日", "操作"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="adm-empty">読み込み中…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={8} className="adm-empty">該当する路線がありません</td></tr>
              ) : (
                pageItems.map((route) => (
                  <tr key={route.id}>
                    <td className="adm-table__id">{route.id}</td>
                    <td className="adm-table__bold">{route.short_name}</td>
                    <td className="adm-table__mute">{route.full_name}</td>
                    <td>
                      {route.color ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span
                            style={{
                              display: "inline-block",
                              width: 14,
                              height: 14,
                              borderRadius: 3,
                              background: route.color,
                              border: "1px solid rgba(0,0,0,.12)",
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontSize: ".75rem", color: "var(--adm-text-muted)", fontFamily: "monospace" }}>
                            {route.color}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--adm-text-muted)" }}>—</span>
                      )}
                    </td>
                    <td className="adm-center adm-table__mute">{route.sort_number}</td>
                    <td>
                      <span className="adm-badge adm-badge--blue">
                        {route.depot?.short_name ?? ""}
                      </span>
                    </td>
                    <td className="adm-table__mute">
                      {route.created_at ? String(route.created_at).slice(0, 10) : ""}
                    </td>
                    <td>
                      <button
                        className="adm-btn adm-btn--default adm-btn--sm"
                        onClick={() => openEdit(route)}
                      >
                        編集
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          current={safePage}
          total={totalPages}
          onChange={setCurrentPage}
          count={filtered.length}
        />
      </div>

      {/* 追加・編集モーダル */}
      <Modal show={modalOpen} title={editRoute ? "路線を編集" : "路線を追加"} onClose={closeModal}>
        <FormField label="略称" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.short_name}
            onChange={(e) => setForm((f) => ({ ...f, short_name: e.target.value }))}
            placeholder="例: 1系統"
            maxLength={50}
          />
        </FormField>
        <FormField label="正式名" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            placeholder="例: 博多駅〜天神行き"
            maxLength={100}
          />
        </FormField>
        <FormField label="カラーコード">
          {/* プリセットパレット */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
            {PRESET_COLORS.map((c) => {
              const selected = form.color.toUpperCase() === c.toUpperCase();
              return (
                <button
                  key={c}
                  type="button"
                  title={c}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  style={{
                    width: 24, height: 24, borderRadius: 5, background: c,
                    border: selected ? "2.5px solid #111" : "1.5px solid transparent",
                    outline: selected ? "1.5px solid #fff" : "none",
                    outlineOffset: -3, cursor: "pointer", flexShrink: 0, padding: 0,
                    transition: "transform .1s",
                    transform: selected ? "scale(1.18)" : "scale(1)",
                  }}
                />
              );
            })}
            <button
              type="button"
              title="クリア"
              onClick={() => setForm((f) => ({ ...f, color: "" }))}
              style={{
                width: 24, height: 24, borderRadius: 5, background: "#fff",
                border: "1px dashed #bbb", cursor: "pointer", flexShrink: 0,
                padding: 0, fontSize: 11, color: "#999", lineHeight: "22px",
              }}
            >✕</button>
          </div>
          {/* 自由入力 */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span
              style={{
                display: "inline-block", width: 28, height: 28, borderRadius: 6,
                background: /^#[0-9A-Fa-f]{6}$/.test(form.color) ? form.color : "#f0f0f0",
                border: "1px solid var(--adm-border)", flexShrink: 0,
              }}
            />
            <input
              type="text"
              className="adm-input"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              placeholder="#RRGGBB（任意・直接入力も可）"
              maxLength={7}
              style={{ flex: 1 }}
            />
          </div>
        </FormField>
        <FormField label="表示順" required>
          <div className="adm-sort-row">
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_number: String(Math.max(0, Number(f.sort_number) - 10)) }))}
            >−10</button>
            <input
              type="number"
              className="adm-input adm-input--full"
              value={form.sort_number}
              onChange={(e) => setForm((f) => ({ ...f, sort_number: e.target.value }))}
              min={0} max={9999}
              style={{ textAlign: "center" }}
            />
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_number: String(Math.min(9999, Number(f.sort_number) + 10)) }))}
            >＋10</button>
          </div>
        </FormField>
        <FormField label="営業所" required>
          <select
            className="adm-select adm-select--full"
            value={form.depot_id}
            onChange={(e) => setForm((f) => ({ ...f, depot_id: e.target.value }))}
          >
            <option value="">選択してください</option>
            {depots.map((d) => (
              <option key={d.id} value={d.id}>{d.short_name}</option>
            ))}
          </select>
        </FormField>
        <ModalActions
          onCancel={closeModal}
          onConfirm={handleSave}
          confirmLabel={saving ? "保存中…" : "保存"}
          disabled={saving}
        />
      </Modal>
    </div>
  );
}
